/**
 * Die Symbole der Faehigkeiten und Passiven (16.10.2026).
 *
 * Erzeugt mit apps/client/scripts/spiel-bilder.mjs. Gefunden werden sie hier
 * automatisch: Liegt fuer eine Faehigkeit ein Bild im Ordner, zeigt das Spiel
 * es; fehlt es, bleibt der schlichte Kreis. Neue Linebounds brauchen deshalb
 * keine Aenderung im Code - nur ihre Bilder.
 *
 * Dateinamen: <Kennung>-<Breite>.<Format>, die Kennung ist die der Faehigkeit
 * aus game-data (z. B. shield_slam), passiv-<Held> (z. B. passiv-vanguard)
 * oder die des Trankes (health_potion, mana_potion) bzw. Gegenstandes
 * (war_blade, revive_totem ...).
 */

import type { Bildquellen } from './menueBilder.js';

const DATEIEN = import.meta.glob<string>('../assets/spiel/faehigkeiten/*.{avif,webp}', {
  eager: true,
  query: '?url',
  import: 'default',
});

/** Kennung -> Format -> Breite -> Adresse. */
const SAMMLUNG = new Map<string, { avif: Map<number, string>; webp: Map<number, string> }>();
for (const [pfad, adresse] of Object.entries(DATEIEN)) {
  const treffer = /\/([a-z_-]+)-(\d+)\.(avif|webp)$/.exec(pfad);
  if (!treffer) continue;
  const [, kennung = '', breite = '0', format] = treffer;
  let eintrag = SAMMLUNG.get(kennung);
  if (!eintrag) {
    eintrag = { avif: new Map(), webp: new Map() };
    SAMMLUNG.set(kennung, eintrag);
  }
  (format === 'avif' ? eintrag.avif : eintrag.webp).set(Number(breite), adresse);
}

function srcset(breiten: Map<number, string>): string {
  return [...breiten.entries()]
    .sort(([a], [b]) => a - b)
    .map(([breite, adresse]) => `${adresse} ${breite}w`)
    .join(', ');
}

function quellen(kennung: string): Bildquellen | undefined {
  const eintrag = SAMMLUNG.get(kennung);
  if (!eintrag || eintrag.webp.size === 0) return undefined;
  const kleinste = Math.min(...eintrag.webp.keys());
  return {
    avif: srcset(eintrag.avif),
    webp: srcset(eintrag.webp),
    ersatz: eintrag.webp.get(kleinste) ?? '',
  };
}

/** Das Symbol einer Faehigkeit - oder nichts, wenn es noch keines gibt. */
export function faehigkeitBild(skillId: string): Bildquellen | undefined {
  return quellen(skillId);
}

/** Das Symbol der Passive eines Helden - oder nichts. */
export function passivBild(heroId: string): Bildquellen | undefined {
  return quellen(`passiv-${heroId}`);
}

/**
 * Die Bilder eines Creeps (18.10.2026): das Kartenbild fuer das Army-Menue
 * und das nahe Kopfbild fuer die kleinen Marken in den Wellen-Reihen.
 * Beide in neutralen Farben (Produktentscheidung vom 18.10.2026): Im Menue
 * schickt man immer die eigenen Creeps, und das fuer beide Seiten.
 */
export function creepKarte(creepId: string): Bildquellen | undefined {
  return quellen(`creep-karte-${creepId}`);
}

export function creepKopf(creepId: string): Bildquellen | undefined {
  return quellen(`creep-kopf-${creepId}`);
}

/** Das Bild eines Gegenstandes (18.10.2026) - oder nichts. */
export function gegenstandBild(itemId: string): Bildquellen | undefined {
  return quellen(itemId);
}

/** Das Bild eines Trankes (16.10.2026) - oder nichts. */
export function trankBild(trankId: string): Bildquellen | undefined {
  return quellen(trankId);
}
